import {
  ClassConstructor,
  instanceToPlain,
  plainToInstance,
} from "class-transformer";
import { AtomEffect } from "recoil";

export interface PersistStorage {
  setItem(key: string, value: string): void | Promise<void>;
  mergeItem?(key: string, value: string): Promise<void>;
  getItem(key: string): null | string | Promise<string>;
}

export interface PersistConfiguration<T> {
  key?: string;
  storage?: PersistStorage;
  klass?: () => ClassConstructor<T>;
}

/**
 * Recoil module to persist state to storage
 *
 * @param config Optional configuration object
 * @param config.key Used as key in local storage, defaults to `recoil-persist`
 * @param config.storage Local storage to use, defaults to `localStorage`
 */
export const recoilPersist = <T>(
  config: PersistConfiguration<T> = {}
): { persistAtom: AtomEffect<any> } => {
  if (typeof window === "undefined") {
    return {
      persistAtom: () => {},
    };
  }

  const { key = "recoil-persist", storage = localStorage, klass } = config;

  const persistAtom: AtomEffect<any> = ({ onSet, node, trigger, setSelf }) => {
    if (trigger === "get") {
      console.log("get state");
      const state = getState();
      if (typeof state.then === "function") {
        state.then((s) => {
          if (s.hasOwnProperty(node.key)) {
            const v = s[node.key];
            if (klass) {
              const t = klass();
              const i = plainToInstance(t, v);
              setSelf(i || new t());
            } else {
              setSelf(v || {});
            }
          }
        });
      } else {
        if (state.hasOwnProperty(node.key)) {
          const v = state[node.key];
          if (klass) {
            const t = klass();
            const i = plainToInstance(t, v);
            setSelf(i || new t());
          } else {
            setSelf(v || {});
          }
        }
      }
    }

    onSet(async (newValue, _, isReset) => {
      const state = getState();
      if (typeof state.then === "function") {
        state.then((s: any) => updateState(newValue, s, node.key, isReset));
      } else {
        updateState(newValue, state, node.key, isReset);
      }
    });
  };

  const updateState = (
    newValue: any,
    state: any,
    key: string,
    isReset: boolean
  ) => {
    if (isReset) {
      delete state[key];
    } else {
      state[key] = newValue;
    }

    setState(state);
  };

  const getState = (): any => {
    const toParse = storage.getItem(key);
    if (toParse === null || toParse === undefined) {
      return {};
    }
    if (typeof toParse === "string") {
      return parseState(toParse);
    }
    if (typeof toParse.then === "function") {
      return toParse.then(parseState);
    }

    return {};
  };

  const parseState = (state: string) => {
    if (state === undefined) {
      return {};
    }
    try {
      return JSON.parse(state);
    } catch (e) {
      console.error(e);
      return {};
    }
  };

  const setState = (state: any): void => {
    try {
      const value = JSON.stringify(state);
    
      storage.setItem(key, value);
    } catch (e) {
      console.error(e);
    }
  };

  return { persistAtom };
};
